import type { IAddonHandler } from './addon-handler.interface';
import type { IAddonHandlerMobile } from './addon-handler-mobile.interface';
import type { AddonIframeOptions } from './configure/addon-iframe.interface';
import type { ICopyPasteImageService } from '@src/slide/application/service/copy-paste/copy-paste.interface';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import type { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import type { IBaseCopyPaste } from '@src/slide/application/service/copy-paste/core/base/base-copy-paste.interface';
import { IImportFileManager } from '@src/slide/application/service/import-file-manager/core/import-file-manager.interface';
import { ILinkManager } from '@src/slide/application/service/link-manager/core/link-manager.interface';
import { IFormulaOcrManager } from '@src/slide/application/service/formula-ocr-manager/core/interface';
import { IEventEmitter } from '@src/slide/infrastructure/module/independent/event-emitter/event-emitter.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';

interface IIframeInfo {
    iframeId?: any;
    embedUrl?: any;
    extraData?: any;
}

type processParagraphList = (paragraphList: any[]) => any[];

export interface IAddonSdkApi {
    event: IEventEmitter<AddonManagerEventsMap>,
    refreshIframe: (srcUrl?: string, embedUrl?: string, displayText?: string) => void,
    updateToolbar: (toolbarData: string, entryPos: string) => void,
    uploadPictureFormulaOcr: () => Promise<string>,
}

/**
 * 插件管理
 */
export interface IAddonManager {
    event: IEventEmitter<AddonManagerEventsMap>;
    addonHandler: IAddonHandler | IAddonHandlerMobile;
    importFileManagerDelegate?: IDynamicService<IImportFileManager>;
    linkManagerDelegate?: ILinkManager;
    workbenchLoaded: boolean;
    /**
     * 设置列表可视幻灯片id缓存，用于局部加载插件占位图展示
     */
    setVisibleSlide(ids: Array<string>): void;

    /**
     * 根据可视幻灯片id缓存，刷新插件占位图
     */
    updateViewSlide(): void;

    /**
     * 根据幻灯片id，刷新插件占位图
     */
    updateSlideAddonScaleView(id: string): void;

    /**
     * 重制刷新的标志状态
     */
    resetSlideUpdateState(): void;

    /**
     * 记录一下激活的model，用于iframe内的插件
     */
    savePrevShapeModel(model, callback): void;

    /**
     * 将之前记录的model关闭，并激活新的model
     */
    updateShapeModel(model?): void;

    /**
     * 更新iframe节点的各种style
     */
    updateIframeCloneDomStyle(iframeWrapper): void;

    /**
     * 插件加载后，刷新工具栏渲染
     * 接完wb后，理论上这个方法应该废弃
     */
    updateToolbar(toolbarData: string, entryPos: string): void;

    /**
     * 检查这个链接是否是个合法的iframe链接
     */
    checkLink(link): Promise<boolean>;

    /**
     * 根据插件 aid 和 menu id 获取 menu 的 report id
     */
    getAddonReportId(aid, menuId);

    /**
     * 根据插件 aid 和 menu id 获取 menu 的 report name
     */
    getAddonReportName(aid, menuId);

    /**
     * 根据插件 aid 和品类的额外信息，动态获取全屏配置
     */
    getAddonFullScreenTitle(aid, extraData);

    shouldRenderFullScreenItem(aid, extraData);

    /**
     * 获取iframe信息
     */
    getIframeInfo(): IIframeInfo;

    /**
     * 插入iframe元素
     */
    insertIframeContainer(iframeInfos: AddonIframeOptions): void;

    /**
     * 检查是否可插入iframe元素
     */
    canAddIframeElement(count?);

    /**
     * 刷新插件iframe
     */
    refreshIframe(srcUrl?: string, embedUrl?: string, displayText?: string);

    /**
     * 文字链<-->卡片态 切换
     */
    changeIframeMode(model, toMode, linkData?, textPositionInfo?, processParagraphList?: processParagraphList);

    /**
     * 插件调用的插入图片接口，目前可能为公式图片或者普通图片
     * @param imageAddonElement 插件返回的图片数据
     */
    insertAddonPicture(imageAddonElement);

    /**
     * 插件调用的更新图片接口，目前只为公式图片
     * @param imageAddonElement 插件返回的图片数据
     */
    updateAddonPicture(imageAddonElement);

    /**
     * 公式识别
     */
    uploadPictureFormulaOcr();

    setCopyPasteManager(copyPasteManager: IDynamicService<IBaseCopyPaste>): void;
    setSelectionService(selectionService: ISelectionService);
    setMenuManagerDelegate(menuManager: IMenuManager);
    setFormulaOcrManager(formulaOcrManager: IDynamicService<IFormulaOcrManager>);
    setCopyPasteImageService(copyPasteImageService: IDynamicService<ICopyPasteImageService>): void;
}

export enum AddonManagerEventsKeys {
    ADDON_HANDLER_IMPORT_READY = 'AddonHandlerImportReady',
    ADDON_INFO_LOAD_READY = 'AddonInfoLoadReady',
    ERROR_NETWORK = 'errorNetwork'
}

export interface AddonManagerEventsMap {
    [AddonManagerEventsKeys.ADDON_HANDLER_IMPORT_READY]: void;
    [AddonManagerEventsKeys.ADDON_INFO_LOAD_READY]: void;
    [AddonManagerEventsKeys.ERROR_NETWORK]: void;
}
